// src/finance/components/YearTable.tsx
// Vue annuelle : tableau des mois saisis + totaux + barres CA réel vs objectif
// (divs proportionnelles — pattern maison, pas de lib de chart).
import React from 'react'
import type { FinanceMonth } from '../../lib/types'
import { effectiveTargetsFor, type MonthlyTargets } from '../targets'
import { fmtEur } from '../format'
import { formatMonthLabel } from './MonthEditor'

const cell: React.CSSProperties = {
  padding: '0.375rem 0.625rem',
  fontSize: '0.8125rem',
  color: 'var(--fg)',
  borderBottom: '1px solid var(--line)',
  textAlign: 'right',
  fontFamily: 'var(--mono)',
}

export function YearTable({ months, targets }: { months: FinanceMonth[]; targets: MonthlyTargets }) {
  if (months.length === 0) return null
  const sorted = [...months].sort((a, b) => a.month.localeCompare(b.month))
  const totals = sorted.reduce(
    (acc, m) => ({
      revenue: acc.revenue + m.revenue,
      expenses: acc.expenses + m.expenses.reduce((s, e) => s + e.amount, 0),
      paid: acc.paid + m.salaryPaid + m.dividendsPaid,
    }),
    { revenue: 0, expenses: 0, paid: 0 },
  )
  // Chaque mois est jugé sur SON objectif : figé à sa création (migration 0012), ou
  // courant s'il est antérieur au gel. Une seule ligne de repère commune aurait
  // rejugé les mois clos à l'aune de la simulation d'aujourd'hui.
  const bars = sorted.map((m) => ({ month: m, target: effectiveTargetsFor(m, targets) }))
  const maxBar = Math.max(targets.revenue, ...bars.map((b) => Math.max(b.month.revenue, b.target.revenue)), 1)

  return (
    <section style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: '10px', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>Vue annuelle</h2>

      {/* Barres CA réel vs objectif */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.5rem', height: '80px' }}>
        {bars.map(({ month: m, target }) => (
          <div
            key={m.id}
            data-testid={`bar-${m.month}`}
            // `unreachable` neutralise l'objectif à 0 (C5) : le présenter comme un
            // objectif ferait lire « objectif 0 € », atteint par n'importe quel CA.
            title={
              target.unreachable
                ? `${formatMonthLabel(m.month)} : ${fmtEur(m.revenue)} — objectif indisponible (cible inatteignable)`
                : `${formatMonthLabel(m.month)} : ${fmtEur(m.revenue)} — objectif ` +
                  `${fmtEur(target.revenue)}${target.frozen ? '' : ' (courant, non figé)'}`
            }
            style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%', position: 'relative' }}
          >
            {/* Pas de repère quand il n'y a pas d'objectif : une ligne au ras du zéro
                se lirait comme une cible atteinte par n'importe quel CA. */}
            {!target.unreachable && (
              <div
                style={{
                  position: 'absolute',
                  bottom: `${(target.revenue / maxBar) * 100}%`,
                  left: 0,
                  right: 0,
                  // Repère en pointillés plus clairs quand l'objectif n'est pas celui du
                  // mois : signal discret que la comparaison est approximative.
                  borderTop: `1px dashed ${target.frozen ? 'var(--bronze)' : 'var(--dim)'}`,
                }}
              />
            )}
            <div
              data-testid="bar-fill"
              style={{
                height: `${(m.revenue / maxBar) * 100}%`,
                // Sans objectif, aucune couleur de verdict : `m.revenue >= 0` passait
                // TOUTES les barres en bronze, la couleur « objectif atteint ».
                background: target.unreachable
                  ? 'var(--line-strong)'
                  : m.revenue >= target.revenue
                    ? 'var(--bronze)'
                    : 'var(--blu)',
                borderRadius: '3px 3px 0 0',
                minHeight: '2px',
              }}
            />
            <span style={{ fontSize: '0.625rem', color: 'var(--dim)', textAlign: 'center', marginTop: '0.25rem' }}>
              {m.month.slice(5)}
            </span>
          </div>
        ))}
      </div>
      <p data-testid="year-table-legend" style={{ fontSize: '0.6875rem', color: 'var(--dim)', margin: 0 }}>
        {targets.unreachable ? (
          <>
            Ligne pointillée : objectif de CA du mois, figé à sa création. Objectif courant :
            cible inatteignable avec ces paramètres fiscaux — les mois sans objectif figé n’ont
            donc aucun repère.
          </>
        ) : (
          <>
            Ligne pointillée : objectif de CA du mois, figé à sa création. Objectif courant :{' '}
            {fmtEur(targets.revenue)} — repère plus pâle sur les mois qui n’ont pas d’objectif figé.
          </>
        )}
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={{ ...cell, textAlign: 'left', color: 'var(--muted)', fontFamily: 'inherit', fontWeight: 500 }}>Mois</th>
              <th style={{ ...cell, color: 'var(--muted)', fontFamily: 'inherit', fontWeight: 500 }}>CA encaissé (HT)</th>
              <th style={{ ...cell, color: 'var(--muted)', fontFamily: 'inherit', fontWeight: 500 }}>Dépenses</th>
              <th style={{ ...cell, color: 'var(--muted)', fontFamily: 'inherit', fontWeight: 500 }}>Versé (sal. + div.)</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((m) => (
              <tr key={m.id}>
                <td style={{ ...cell, textAlign: 'left', fontFamily: 'inherit', textTransform: 'capitalize' }}>{formatMonthLabel(m.month)}</td>
                <td style={cell}>{fmtEur(m.revenue)}</td>
                <td style={cell}>{fmtEur(m.expenses.reduce((s, e) => s + e.amount, 0))}</td>
                <td style={cell}>{fmtEur(m.salaryPaid + m.dividendsPaid)}</td>
              </tr>
            ))}
            <tr>
              <td style={{ ...cell, textAlign: 'left', fontFamily: 'inherit', fontWeight: 600, borderBottom: 'none' }}>Total</td>
              <td style={{ ...cell, fontWeight: 600, borderBottom: 'none' }}>{fmtEur(totals.revenue)}</td>
              <td style={{ ...cell, fontWeight: 600, borderBottom: 'none' }}>{fmtEur(totals.expenses)}</td>
              <td style={{ ...cell, fontWeight: 600, borderBottom: 'none' }}>{fmtEur(totals.paid)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  )
}
